import React from "react";
import { Link } from "react-router-dom";

/**
 * Credits page — sits at /credits, linked from the footer.
 *
 * Purpose
 * -------
 * Provides friendly, human attribution for the open-data sources that
 * power the HustleSuite Landmarks database (toilets, parks, transport
 * stops, drinking fountains, hospitals, libraries, service stations
 * etc.). Wording deliberately warm rather than legalistic — the
 * strict licence text is included at the bottom for completeness.
 */
export default function CreditsPage() {
  return (
    <div
      data-testid="credits-page"
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-800"
    >
      <div className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-3">
          Credits & attribution
        </p>
        <h1
          data-testid="credits-heading"
          className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-6"
        >
          Standing on the shoulders of open data.
        </h1>
        <p className="text-lg text-slate-600 mb-10 leading-relaxed">
          A big thank-you to the volunteers, communities and public agencies
          whose freely-shared datasets help power the little things across
          the HustleSuite of products — like finding the nearest public
          toilet, the closest playground, or the next bus stop for your
          dinner reservation.
        </p>

        <section
          data-testid="credits-section-osm"
          className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            OpenStreetMap contributors
          </h2>
          <p className="text-slate-600 leading-relaxed mb-2">
            Much of our map of everyday places — parks, playgrounds,
            drinking fountains, bus stops, train and tram stations,
            ferry terminals, hospitals, libraries and service stations —
            comes from the tireless work of the OpenStreetMap community.
          </p>
          <p className="text-sm text-slate-500">
            © OpenStreetMap contributors, made available under the{" "}
            <a
              className="text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
              href="https://opendatacommons.org/licenses/odbl/"
              target="_blank" rel="noreferrer"
            >
              Open Database Licence (ODbL)
            </a>.
          </p>
        </section>

        <section
          data-testid="credits-section-datagov"
          className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            National Public Toilet Map — Australian Government
          </h2>
          <p className="text-slate-600 leading-relaxed mb-2">
            Thank you to the Department of Health &amp; Aged Care and{" "}
            <a
              className="text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
              href="https://data.gov.au/data/dataset/toiletmapexport"
              target="_blank" rel="noreferrer"
            >
              data.gov.au
            </a>{" "}
            for maintaining the National Public Toilet Map — the source
            of accessibility, opening-hours and facility details for
            public toilets right across Australia.
          </p>
          <p className="text-sm text-slate-500">
            Used under a{" "}
            <a
              className="text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
              href="https://creativecommons.org/licenses/by/4.0/"
              target="_blank" rel="noreferrer"
            >
              Creative Commons Attribution 4.0 (CC-BY 4.0)
            </a>{" "}
            licence.
          </p>
        </section>

        <section
          data-testid="credits-section-gtfs"
          className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            Public transport agencies (GTFS feeds)
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Bus, train, tram and ferry stop and route information is
            provided by Australian state transport authorities (including
            Translink QLD, Transport for NSW, Public Transport Victoria,
            TransLink SA, Transperth and TasBus) through their published
            GTFS feeds. Thanks for keeping open transit data alive — it
            makes real-time arrival hints for restaurant bookings and
            "leave-home-now" reminders possible.
          </p>
        </section>

        <section
          data-testid="credits-section-au-suburbs"
          className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            Australia Post &amp; ABS — suburbs, postcodes &amp; locality
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Suburb / postcode / state reference data is derived from
            publicly published Australia Post and Australian Bureau of
            Statistics locality lists.
          </p>
        </section>

        <section
          data-testid="credits-section-general"
          className="bg-white border border-slate-200 rounded-2xl p-6 mb-10 shadow-sm"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            And to everyone else…
          </h2>
          <p className="text-slate-600 leading-relaxed">
            To every volunteer editor, council data officer and open-data
            advocate whose work sits quietly behind our little maps and
            search boxes — thank you. HustleSuite is better because of
            you.
          </p>
        </section>

        <div className="text-sm text-slate-500 border-t border-slate-200 pt-6">
          Something missing or attributed wrong? Please email{" "}
          <a
            className="text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
            href="mailto:credits@hustlesuite.au"
            data-testid="credits-email-link"
          >
            credits@hustlesuite.au
          </a>{" "}
          and we&apos;ll fix it fast. See our{" "}
          <Link
            to="/terms"
            className="text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
            data-testid="credits-terms-link"
          >
            Terms
          </Link>{" "}
          for the full licensing notes.
        </div>
      </div>
    </div>
  );
}
